import { LMSProvider } from '../types/lms';

export interface InfiniteCampusStudent {
  personID: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  grade: number;
  studentNumber: string;
  email?: string;
  phone?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  emergencyContacts?: Array<{
    name: string;
    relationship: string;
    phone: string;
    email?: string;
  }>;
}

export interface InfiniteCampusAttendance {
  personID: string;
  date: string;
  period: number;
  attendanceCode: string;
  attendanceStatus: 'present' | 'absent' | 'tardy' | 'excused';
  minutes?: number;
  note?: string;
  lastUpdated: string;
}

export interface InfiniteCampusSection {
  sectionID: string;
  courseName: string;
  courseCode: string;
  roomNumber?: string;
  period: number;
  teacherID: string;
  teacherName: string;
  enrolledStudents: string[];
  startDate: string;
  endDate: string;
}

export interface InfiniteCampusGrade {
  personID: string;
  sectionID: string;
  assignmentID: string;
  assignmentName: string;
  score: number;
  maxPoints: number;
  letterGrade?: string;
  dateAssigned: string;
  dateDue: string;
  category: string;
  weight?: number;
}

export interface InfiniteCampusTeacher {
  staffID: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  department?: string;
  sections: string[];
}

export interface InfiniteCampusEnrollment {
  personID: string;
  sectionID: string;
  enrollmentDate: string;
  dropDate?: string;
  status: 'active' | 'dropped' | 'withdrawn';
}

export class InfiniteCampusService {
  private provider: LMSProvider;

  constructor(provider: LMSProvider) {
    this.provider = provider;
  }

  private async makeRequest<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${this.provider.apiUrl}/api/v1${endpoint}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${this.provider.apiKey}`,
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...options?.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Infinite Campus API error (${response.status}): ${errorText}`);
    }

    return response.json();
  }

  // Get all students
  async getStudents(params?: {
    grade?: number;
    active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<InfiniteCampusStudent[]> {
    const searchParams = new URLSearchParams();
    
    if (params?.grade !== undefined) {
      searchParams.append('grade', params.grade.toString());
    }
    if (params?.active !== undefined) {
      searchParams.append('active', params.active.toString());
    }
    if (params?.limit) {
      searchParams.append('limit', params.limit.toString());
    }
    if (params?.offset) {
      searchParams.append('offset', params.offset.toString());
    }

    const endpoint = `/students?${searchParams.toString()}`;
    const response = await this.makeRequest<{ students: InfiniteCampusStudent[] }>(endpoint);
    return response.students || [];
  }

  // Get student by ID
  async getStudent(personID: string): Promise<InfiniteCampusStudent> {
    const endpoint = `/students/${personID}`;
    return this.makeRequest<InfiniteCampusStudent>(endpoint);
  }

  // Get attendance for date range
  async getAttendance(params: {
    startDate: string;
    endDate: string;
    studentIDs?: string[];
    sectionID?: string;
  }): Promise<InfiniteCampusAttendance[]> {
    const searchParams = new URLSearchParams();
    searchParams.append('startDate', params.startDate);
    searchParams.append('endDate', params.endDate);
    
    if (params.studentIDs) {
      params.studentIDs.forEach(id => searchParams.append('studentID', id));
    }
    if (params.sectionID) {
      searchParams.append('sectionID', params.sectionID);
    }

    const endpoint = `/attendance?${searchParams.toString()}`;
    const response = await this.makeRequest<{ attendance: InfiniteCampusAttendance[] }>(endpoint);
    return response.attendance || [];
  }

  // Submit attendance
  async submitAttendance(attendanceData: Array<{
    personID: string;
    sectionID: string;
    date: string;
    period: number;
    attendanceCode: string;
    minutes?: number;
    note?: string;
  }>): Promise<boolean> {
    try {
      const endpoint = '/attendance/submit';
      await this.makeRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify({ attendance: attendanceData }),
      });
      return true;
    } catch (error) {
      console.error('Error submitting attendance to Infinite Campus:', error);
      return false;
    }
  }

  // Get sections (classes)
  async getSections(params?: {
    teacherID?: string;
    active?: boolean;
    term?: string;
  }): Promise<InfiniteCampusSection[]> {
    const searchParams = new URLSearchParams();
    
    if (params?.teacherID) {
      searchParams.append('teacherID', params.teacherID);
    }
    if (params?.active !== undefined) {
      searchParams.append('active', params.active.toString());
    }
    if (params?.term) {
      searchParams.append('term', params.term);
    }

    const endpoint = `/sections?${searchParams.toString()}`;
    const response = await this.makeRequest<{ sections: InfiniteCampusSection[] }>(endpoint);
    return response.sections || [];
  }

  // Get section details
  async getSection(sectionID: string): Promise<InfiniteCampusSection> {
    const endpoint = `/sections/${sectionID}`;
    return this.makeRequest<InfiniteCampusSection>(endpoint);
  }

  // Get grades for a section
  async getSectionGrades(sectionID: string, params?: {
    assignmentID?: string;
    studentIDs?: string[];
  }): Promise<InfiniteCampusGrade[]> {
    const searchParams = new URLSearchParams();
    
    if (params?.assignmentID) {
      searchParams.append('assignmentID', params.assignmentID);
    }
    if (params?.studentIDs) {
      params.studentIDs.forEach(id => searchParams.append('studentID', id));
    }

    const endpoint = `/sections/${sectionID}/grades?${searchParams.toString()}`;
    const response = await this.makeRequest<{ grades: InfiniteCampusGrade[] }>(endpoint);
    return response.grades || [];
  }

  // Submit grades
  async submitGrades(sectionID: string, grades: Array<{
    personID: string;
    assignmentID: string;
    score: number;
    letterGrade?: string;
    comment?: string;
  }>): Promise<boolean> {
    try {
      const endpoint = `/sections/${sectionID}/grades/submit`;
      await this.makeRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify({ grades }),
      });
      return true;
    } catch (error) {
      console.error('Error submitting grades to Infinite Campus:', error);
      return false;
    }
  }

  // Get teachers
  async getTeachers(params?: {
    active?: boolean;
    department?: string;
  }): Promise<InfiniteCampusTeacher[]> {
    const searchParams = new URLSearchParams();
    
    if (params?.active !== undefined) {
      searchParams.append('active', params.active.toString());
    }
    if (params?.department) {
      searchParams.append('department', params.department);
    }

    const endpoint = `/staff/teachers?${searchParams.toString()}`;
    const response = await this.makeRequest<{ teachers: InfiniteCampusTeacher[] }>(endpoint);
    return response.teachers || [];
  }

  // Get teacher details
  async getTeacher(staffID: string): Promise<InfiniteCampusTeacher> {
    const endpoint = `/staff/teachers/${staffID}`;
    return this.makeRequest<InfiniteCampusTeacher>(endpoint);
  }

  // Get enrollments
  async getEnrollments(params?: {
    sectionID?: string;
    studentID?: string;
    active?: boolean;
  }): Promise<InfiniteCampusEnrollment[]> {
    const searchParams = new URLSearchParams();
    
    if (params?.sectionID) {
      searchParams.append('sectionID', params.sectionID);
    }
    if (params?.studentID) {
      searchParams.append('studentID', params.studentID);
    }
    if (params?.active !== undefined) {
      searchParams.append('active', params.active.toString());
    }

    const endpoint = `/enrollments?${searchParams.toString()}`;
    const response = await this.makeRequest<{ enrollments: InfiniteCampusEnrollment[] }>(endpoint);
    return response.enrollments || [];
  }

  // Test connection
  async testConnection(): Promise<boolean> {
    try {
      const endpoint = '/health';
      await this.makeRequest(endpoint);
      return true;
    } catch (error) {
      console.error('Infinite Campus connection test failed:', error);
      return false;
    }
  }

  // Get attendance summary for a date range
  async getAttendanceSummary(params: {
    startDate: string;
    endDate: string;
    sectionID?: string;
  }): Promise<{
    totalDays: number;
    presentDays: number;
    absentDays: number;
    tardyDays: number;
    excusedDays: number;
    attendanceRate: number;
    byStudent: Record<string, {
      present: number;
      absent: number;
      tardy: number;
      excused: number;
      rate: number;
    }>;
  }> {
    const attendance = await this.getAttendance(params);
    
    const summary = {
      totalDays: 0,
      presentDays: 0,
      absentDays: 0,
      tardyDays: 0,
      excusedDays: 0,
      attendanceRate: 0,
      byStudent: {} as Record<string, {
        present: number;
        absent: number;
        tardy: number;
        excused: number;
        rate: number;
      }>
    };

    const studentCounts: Record<string, Record<string, number>> = {};

    attendance.forEach(record => {
      if (!studentCounts[record.personID]) {
        studentCounts[record.personID] = {
          present: 0,
          absent: 0,
          tardy: 0,
          excused: 0,
          total: 0
        };
      }

      studentCounts[record.personID][record.attendanceStatus]++;
      studentCounts[record.personID].total++;

      // Overall counts
      switch (record.attendanceStatus) {
        case 'present':
          summary.presentDays++;
          break;
        case 'absent':
          summary.absentDays++;
          break;
        case 'tardy':
          summary.tardyDays++;
          break;
        case 'excused':
          summary.excusedDays++;
          break;
      }
    });

    summary.totalDays = summary.presentDays + summary.absentDays + summary.tardyDays + summary.excusedDays;
    summary.attendanceRate = summary.totalDays > 0 ? 
      ((summary.presentDays + summary.tardyDays + summary.excusedDays) / summary.totalDays) * 100 : 0;

    // Calculate per-student rates
    Object.entries(studentCounts).forEach(([studentID, counts]) => {
      summary.byStudent[studentID] = {
        present: counts.present,
        absent: counts.absent,
        tardy: counts.tardy,
        excused: counts.excused,
        rate: counts.total > 0 ? 
          ((counts.present + counts.tardy + counts.excused) / counts.total) * 100 : 0
      };
    });

    return summary;
  }

  // Sync all student data
  async syncAllStudentData(): Promise<{
    students: InfiniteCampusStudent[];
    sections: InfiniteCampusSection[];
    enrollments: InfiniteCampusEnrollment[];
    teachers: InfiniteCampusTeacher[];
  }> {
    try {
      const [students, sections, enrollments, teachers] = await Promise.all([
        this.getStudents({ active: true }),
        this.getSections({ active: true }),
        this.getEnrollments({ active: true }),
        this.getTeachers({ active: true })
      ]);

      return {
        students,
        sections,
        enrollments,
        teachers
      };
    } catch (error) {
      console.error('Error syncing Infinite Campus student data:', error);
      throw error;
    }
  }

  // Get attendance alerts (students with poor attendance)
  async getAttendanceAlerts(params: {
    startDate: string;
    endDate: string;
    threshold?: number; // Attendance rate threshold (default 90%)
  }): Promise<Array<{
    student: InfiniteCampusStudent;
    attendanceRate: number;
    totalAbsences: number;
    recentAbsences: number;
  }>> {
    const threshold = params.threshold || 90;
    const students = await this.getStudents({ active: true });
    const alerts = [];

    for (const student of students) {
      try {
        const attendance = await this.getAttendance({
          startDate: params.startDate,
          endDate: params.endDate,
          studentIDs: [student.personID]
        });

        const present = attendance.filter(a => a.attendanceStatus === 'present').length;
        const total = attendance.length;
        const rate = total > 0 ? (present / total) * 100 : 100;

        if (rate < threshold) {
          const totalAbsences = attendance.filter(a => a.attendanceStatus === 'absent').length;
          const recentDate = new Date();
          recentDate.setDate(recentDate.getDate() - 7);
          const recentAbsences = attendance.filter(a => 
            a.attendanceStatus === 'absent' && 
            new Date(a.date) >= recentDate
          ).length;

          alerts.push({
            student,
            attendanceRate: rate,
            totalAbsences,
            recentAbsences
          });
        }
      } catch (error) {
        console.warn(`Could not get attendance for student ${student.personID}:`, error);
      }
    }

    return alerts.sort((a, b) => a.attendanceRate - b.attendanceRate);
  }
}

export default InfiniteCampusService;